import { memo, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { entityAPI } from '../../services/api'
import galleryCache from '../../services/galleryCache'
import { createLogger } from '../../utils/logger'
import { useAuth } from '../../context/AuthContext'
import { useFavorites } from '../../hooks/useFavorites'
import { useEntityInsights } from '../../hooks/useEntityInsights'
import EntityPopupHeader from './popup/EntityPopupHeader'
import EntityPopupInfo from './popup/EntityPopupInfo'
import EntityPopupActions from './popup/EntityPopupActions'
import ConfirmationForm from './popup/ConfirmationForm'
import { formatRelativeTime } from '../../utils/dateFormatting'
import { trackButtonClick } from '../../utils/analytics'

const logger = createLogger('EntityPopupContent')

/**
 * EntityPopupContent - Modular, reusable popup content for map markers
 * 
 * Features:
 * - Glassmorphism design matching control panel
 * - Two variants: 'normal' (HomePageMap) and 'entity' (EntityDetailMap)
 * - Optimized layout with hero images
 * - Consistent styling across all map instances
 * 
 * @param {Object} props
 * @param {Object} props.entity - Entity data
 * @param {string} props.variant - 'normal' | 'entity'
 * @param {number} props.confirmationCount - Number of confirmations
 * @param {Function} props.formatSightingDate - Date formatting function
 * @param {boolean} props.isEntityMode - Whether in entity detail mode
 * @param {Object} props.t - Translation function
 * 
 * // Entity mode specific props
 * @param {boolean} props.isOwnSighting - User owns this sighting
 * @param {boolean} props.showConfirmForm - Show confirmation form
 * @param {Function} props.handleConfirm - Confirm button handler
 * @param {Function} props.handleSubmitConfirmation - Submit confirmation handler
 * @param {Function} props.handleCancelConfirmation - Cancel confirmation handler
 * @param {Array} props.confirmImages - Confirmation images
 * @param {string} props.confirmNotes - Confirmation notes
 * @param {Function} props.setConfirmNotes - Set confirmation notes
 * @param {string|null} props.confirmEntityStatus - Open/closed status for confirmation
 * @param {Function} props.setConfirmEntityStatus - Set confirmation entity status
 * @param {boolean} props.isSubmitting - Submitting state
 * @param {Component} props.ImageUpload - Image upload component
 * @param {Function} props.setConfirmImages - Set confirmation images
 * 
 * @idempotent - Same props always produce same output
 * @atomic - Single responsibility: render popup content
 */
const EntityPopupContent = memo(function EntityPopupContent({
  entity,
  onClosePopup,
  variant = 'normal',
  confirmationCount,
  isEntityMode = false,
  sightingId,
  sightingUserId,
  // Entity mode specific props
  isOwnSighting = false,
  showConfirmForm = false,
  handleConfirm,
  handleSubmitConfirmation,
  handleCancelConfirmation,
  confirmImages = [],
  confirmNotes = '',
  setConfirmNotes,
  confirmEntityStatus = null,
  setConfirmEntityStatus,
  isSubmitting = false,
  ImageUpload,
  setConfirmImages,
  lat,
  lng,
  userLocation,
  isImmersiveMode = false,
  onTabChange,
  onSpotEntity,
  onRequestBook = null,
}) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isFavorite, toggleFavorite, isTogglingFavorite } = useFavorites(user, userLocation)
  const { peakDow, peakHour } = useEntityInsights(entity.entityId || null)
  const [galleryPrimaryImage, setGalleryPrimaryImage] = useState(null)
  const [isFetchingGallery, setIsFetchingGallery] = useState(false)
  const hasAttemptedFetch = useRef(false)
  const isUnconfirmed = confirmationCount === 0

  // Memoize placeholder check to prevent unnecessary recalculations
  const isPlaceholderImage = useMemo(() => {
    return entity.imageUrl && (
      entity.imageUrl.includes('unsplash.com') || 
      entity.imageUrl.includes('placeholder')
    )
  }, [entity.imageUrl])

  // Use centralized date formatting utility
  const formatSightingDate = formatRelativeTime


  // Fetch gallery primary image if entity doesn't have an image OR has a placeholder image
  // Use ref to prevent multiple fetch attempts for the same entity
  useEffect(() => {
    const fetchGalleryImage = async () => {
      // Skip if already attempted fetch for this entity
      if (hasAttemptedFetch.current) {
        return
      }

      // Fetch if: no image OR placeholder image, and we haven't fetched yet
      const shouldFetch = (!entity.imageUrl || isPlaceholderImage) && !galleryPrimaryImage && !isFetchingGallery && entity.entityId
      
      if (shouldFetch) {
        hasAttemptedFetch.current = true
        setIsFetchingGallery(true)
        try {
          // Use gallery cache service to prevent duplicate requests
          const gallery = await galleryCache.getGallery(entity.entityId)
          const primaryImage = gallery.find(img => img.isPrimary)
          const imageToUse = primaryImage || gallery[0]
          if (imageToUse) {
            setGalleryPrimaryImage(imageToUse.imageUrl)
          }
        } catch (error) {
          // Silently fail - just don't show an image
          logger.debug('Could not fetch gallery for entity:', { entityId: entity.entityId, error: error.message })
        } finally {
          setIsFetchingGallery(false)
        }
      }
    }
    fetchGalleryImage()
  }, [entity.entityId, entity.imageUrl, isPlaceholderImage, galleryPrimaryImage, isFetchingGallery])

  // Reset fetch attempt flag when entity changes
  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [entity.entityId])

  // Try to get image: gallery images (primary > first) > entity.imageUrl (only if not placeholder) > fetched gallery image
  const primaryGalleryImage = entity.gallery?.find(img => img.isPrimary)
  const firstGalleryImage = entity.gallery?.[0]
  
  // Prefer gallery images over placeholder images
  const displayImageUrl = primaryGalleryImage?.imageUrl || 
                         firstGalleryImage?.imageUrl || 
                         galleryPrimaryImage || 
                         (!isPlaceholderImage ? entity.imageUrl : null)

  // Navigation handler for normal mode
  const handleNavigate = useCallback((entityId) => {
    if (entityId) {
      navigate(`/entity/${entityId}`)
    } else {
      navigate(`/entity/${entity.entityId}`)
    }
  }, [navigate, entity.entityId])

  // Book handler:
  // - entity variant (EntityDetailMap): dispatch custom event so EntityDisplayPanel
  //   catches it and shows BookingInlinePanel inside the panel (no overlay)
  // - normal variant (HomePageMap): call onRequestBook so NormalModeMarker renders
  //   the modal outside the Popup tree (survives popup close)
  const handleBook = useCallback(() => {
    const targetId = entity.entityId || entity.id || entity.entity_id
    logger.info('Book button clicked in popup', { variant, targetId, hasUser: !!user, hasOnClosePopup: !!onClosePopup })
    trackButtonClick('popup_book_service', 'EntityPopupContent', { variant, entityId: targetId })
    if (!user) {
      if (onClosePopup) onClosePopup()
      if (isImmersiveMode && onTabChange) onTabChange('auth')
      else navigate('/login', { state: { from: window.location.pathname } })
      return
    }
    if (variant === 'entity') {
      if (onClosePopup) onClosePopup()
      logger.info('Dispatching trckit:book event', { entityId: targetId })
      window.dispatchEvent(new CustomEvent('trckit:book', { detail: { entityId: targetId } }))
    } else if (onRequestBook) {
      onRequestBook(targetId, entity.name)
    }
  }, [user, entity, variant, onClosePopup, isImmersiveMode, onTabChange, navigate, onRequestBook])

  return (
    <div className="w-full rounded-xl">
      {/* Header with image and title */}
      <EntityPopupHeader 
        displayImageUrl={displayImageUrl}
        variant={variant}
        entity={entity}
        onNavigate={handleNavigate}
      />

      {/* Content */}
      <div className="p-3">

        {/* Information section */}
        <EntityPopupInfo 
          variant={variant}
          entity={entity}
          confirmationCount={confirmationCount}
          formatSightingDate={formatSightingDate}
          lat={lat}
          lng={lng}
          peakDow={peakDow}
          peakHour={peakHour}
        />

        {/* Footer Actions */}
        {!showConfirmForm ? (
          <EntityPopupActions 
            variant={variant}
            entity={entity}
            isOwnSighting={isOwnSighting}
            sightingId={sightingId}
            sightingUserId={sightingUserId}
            onNavigate={handleNavigate}
            onConfirm={handleConfirm}
            user={user}
            isFavorite={isFavorite(entity.entityId || entity.id)}
            toggleFavorite={toggleFavorite}
            isTogglingFavorite={isTogglingFavorite}
            entityId={entity.entityId || entity.id}
            isImmersiveMode={isImmersiveMode}
            onTabChange={onTabChange}
            navigate={navigate}
            onClosePopup={onClosePopup}
            onSpotEntity={onSpotEntity}
            onBook={handleBook}
          />
        ) : (
          <ConfirmationForm 
            confirmNotes={confirmNotes}
            setConfirmNotes={setConfirmNotes}
            confirmImages={confirmImages}
            setConfirmImages={setConfirmImages}
            entityStatus={confirmEntityStatus}
            onEntityStatusChange={setConfirmEntityStatus}
            isSubmitting={isSubmitting}
            ImageUpload={ImageUpload}
            onSubmit={handleSubmitConfirmation}
            onCancel={handleCancelConfirmation}
          />
        )}
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Return true to SKIP re-render, false to re-render
  
  // If entity ID changes, definitely re-render
  if (prevProps.entity?.id !== nextProps.entity?.id) {
    return false
  }
  
  // Compare all relevant props that affect render output
  return (
    prevProps.entity?.entityId === nextProps.entity?.entityId &&
    prevProps.entity?.imageUrl === nextProps.entity?.imageUrl &&
    prevProps.entity?.name === nextProps.entity?.name &&
    prevProps.entity?.gallery === nextProps.entity?.gallery &&
    prevProps.confirmationCount === nextProps.confirmationCount &&
    prevProps.variant === nextProps.variant &&
    prevProps.isEntityMode === nextProps.isEntityMode &&
    prevProps.isOwnSighting === nextProps.isOwnSighting &&
    prevProps.showConfirmForm === nextProps.showConfirmForm &&
    prevProps.isSubmitting === nextProps.isSubmitting &&
    prevProps.lat === nextProps.lat &&
    prevProps.lng === nextProps.lng &&
    prevProps.confirmNotes === nextProps.confirmNotes &&
    prevProps.confirmImages?.length === nextProps.confirmImages?.length
  )
})

export default EntityPopupContent
